import {Auth, Connect, Message} from '@genesislcap/foundation-comms';
import {
  CriteriaSegmentedControl,
  CriteriaSegmentedControlOption,
  Serialisers,
} from '@genesislcap/foundation-criteria';
import { Filters, StringRendererOptions, UiSchema } from '@genesislcap/foundation-forms';
import { customElement, GenesisElement, observable, volatile, inject } from '@genesislcap/web-core';
import { BondBiddingStyles as styles } from './bond-bidding.styles';
import { BondBiddingTemplate as template } from './bond-bidding.template';
import { TrancheDetailFlyout } from './tranche-detail-flyout';
import { GridPro, LabelValueStatusBarComponent, GridProStatusBarTypes } from '@genesislcap/rapid-grid-pro';
import { sideBar } from '../../../../mbm/utils';
import { GridApi, ColumnApi } from '@ag-grid-community/core';
import { hasRowPendingChanges } from '../../../../mbm/utils/bulk-response-upload/submit-eligibility';
import {
  formatDate,
  formatNumeric,
  formatTime,
  submitBondBidsBatch,
  getSubmitAllContext,
  getRowDataAfterSingleSubmit,
  clearInvalidDueWithinInput,
  appendBidTimeMsCriteria,
  applyBondBiddingFilterCriteria,
  extractCusipEqualityValues,
  buildWatchlistMergedCriteria,
  cusipsFromFilterData,
} from '../../../../mbm/utils/bond-bid-utils';
import {
  sanitizeCriteriaString,
  stripBidTimeHoursFormFieldsFromCriteria,
  stripBidTimeMsCriteriaFromCriteria,
  stripCorrelationIdFromCriteria,
} from '../../../../mbm/utils/bond-bid-criteria-format-utils';
import { getLiveActivityColumns } from '../../../../mbm/utils/column-config';
import { downloadAll, fetchMergedExportRows } from './bond-bidding-download';
import {
  parseUploadFile,
  validateAgainstMergedRows,
  applyToGrid,
  prepareUploadBondIds,
  registerUploadBondFilter,
  getUploadModeReferenceIdSource,
} from './bond-bidding-upload';
import { buildCorrelationIdFilter, showBulkToast, showBackendUnavailableToast } from './bond-bidding-bulk-utils';
import {
  BOND_BIDDING_MESSAGES,
  BOND_BIDDING_TOAST_TITLE,
} from './bond-bidding-messages';
import { UploadValidationFlyout } from './upload-validation-flyout';
import {
  BondBiddingFilterCache,
  SavedFilterState,
  restoreSavedFilterDropdown,
} from './bond-bidding-filter-cache';

@customElement({
  name: 'bond-bidding-route',
  template,
  styles,
})
export class BondBiddingRoute extends GenesisElement {
  private filterCache: BondBiddingFilterCache | null = null;
  private pendingRestoreState: SavedFilterState | null = null;
  /** Tracks restore phase to suppress side effects during initialization. */
  private restorePhase: 'idle' | 'criteria' | 'dropdown' = 'idle';
  private applyTimeFiltersOnNextChange = false;

  private getFilterCache(): BondBiddingFilterCache {
    if (!this.filterCache) {
      const user = this.auth?.currentUser?.userName || 'anonymous';
      this.filterCache = new BondBiddingFilterCache(user);
    }
    return this.filterCache;
  }

  @Auth auth: Auth;
  @observable public bondDetailsOpen: boolean = false;
  @observable latestBiddingGridStateKey: string = 'bond-bidding-grid-state';
  @observable selectedBondId: string | null = null;
  @observable filterValue: string = '';
  @observable filtersFlyoutOpen: boolean = false;
  @observable filter: Filters;
  @observable filterName: string = 'Apply Filters';
  @observable moreRows: boolean = false;
  @observable hasClickedMoreRows: boolean = false;
  @observable hasClickedFilters: boolean = false;
  @observable uploadValidationOpen: boolean = false;
  @observable hasEligibleBidsForSubmitAll: boolean = true;
  @observable isUploadInProgress: boolean = false;
  @observable isDownloadInProgress: boolean = false;
  @observable isSubmitAllInProgress: boolean = false;
  /**
   * Set after a successful EVENT_UPLOAD_BOND_FILTER. While non-null:
   *  - the grid datasource binds to UPLOAD_FILTERED_BONDS (server-side correlation slice)
   *  - filterValue carries a sticky `CORRELATION_ID == '<uuid>'` clause merged with all other criteria
   * Cleared on Clear All Filters and on browser tab refresh (sessionStorage restore path).
   */
  @observable uploadCorrelationId: string | null = null;
  /** Unmounts the grid datasource while upload ↔ live mode is switching. */
  @observable isBondsDatasourceSuspended = false;
  /**
   * Gates initial datasource mount until session filter restore completes.
   * Only false on connect when sessionStorage has saved filters — avoids
   * overlapping ACTIVE_MUNICIPAL_BONDS subscriptions (duplicate BOND_ID rows).
   * Upload / clear-all paths leave this true; they use transitionBondsDatasource.
   */
  @observable isBondsCriteriaReady = true;

  /**
   * Firm For range in hours → criteria on view field `FIRM_FOR` (ms) for `MUNICIPAL_BOND_VIEW_TEST`.
   */
  @observable bidTimeHoursMin: number | null = null;
  @observable bidTimeHoursMax: number | null = null;

  trancheDetail: TrancheDetailFlyout;
  uploadValidationFlyout: UploadValidationFlyout;
  uploadFileInput: HTMLInputElement;
  bondFlyout: HTMLElement;
  foundationFilters: Filters;

  grid: GridPro;
  private gridApi: GridApi;
  private columnApi: ColumnApi;
  private statusRefreshInterval: any = null;
  private activityMap = new Map()
  private rowRefMap = new Map()
  /** Coalesces CPTY_BOND_ACTIVITY stream bursts into one grid flush per frame. */
  private bidActivityFlushRafId: number | null = null;
  private static readonly BID_ACTIVITY_FIELDS = [
    'BID_ID',
    'BID_PRICE',
    'BID_PRICE_TYPE',
    'BID_STATUS',
    'FEEDBACK',
    'CALCULATED_BID_PRICE',
    'CALCULATED_YIELD',
  ] as const;
  private cachedLiveActivityColumns: any[] | null = null;
  private cachedLiveActivityColumnsPlatformView: boolean | null = null;

  @observable timeModeCriteriaOptions: CriteriaSegmentedControlOption[] = [
    { label: 'Due', field: 'STATUS', value: 'DUE_IN', serialiser: Serialisers.EQ },
    { label: 'Firm', field: 'STATUS', value: 'FIRM_IN', serialiser: Serialisers.EQ },
  ];

  @observable bidModeCriteriaOptions: CriteriaSegmentedControlOption[] = [
    { label: 'Bid', field: 'BID_PRICE', value: null, serialiser: Serialisers.NE }, // BID_PRICE != null
    { label: 'Not Bid', field: 'BID_PRICE', value: null, serialiser: Serialisers.EQ }, // BID_PRICE == null
  ];

  @observable watchlistOptions: CriteriaSegmentedControlOption[] = [
    { label: 'Watchlist', field: 'WATCHLIST_NAME', value: 'WATCHLIST', serialiser: Serialisers.EQ },
  ];

  @inject(Connect) connect: Connect;

  @observable watchlistValue = '';
  private watchlistCusipSet: any;
  private watchlistSubscription: any;
  /** Flyout/saved-filter CUSIPs. Kept while watchlist ON so OFF can restore them; updated when flyout CUSIP changes. */
  private baseCusips: string[] | null = null;

  get isPlatformActivityView(): boolean {
    return !!this.auth?.currentUser?.hasPermission('ViewPlatformActivity');
  }

  private get api(): GridApi | undefined {
    return this.grid?.gridApi ?? this.gridApi ?? undefined;
  }

  get liveActivityColumns() {
    const isPlatformView = !!this.auth?.currentUser?.hasPermission('ViewPlatformActivity');
    if (
      this.cachedLiveActivityColumns &&
      this.cachedLiveActivityColumnsPlatformView === isPlatformView
    ) {
      return this.cachedLiveActivityColumns;
    }

    this.cachedLiveActivityColumns = getLiveActivityColumns(this.auth?.currentUser, {
      onBidSubmitted: (bondId, bidPrice, bidPriceType) =>
        this.handleBidSubmitted(bondId, bidPrice, bidPriceType),
    });
    this.cachedLiveActivityColumnsPlatformView = isPlatformView;
    return this.cachedLiveActivityColumns;
  }

  filterValueChanged() {
    if (this.isBondsDatasourceSuspended) {
      return;
    }
    if (this.restorePhase !== 'idle') {
      // Restore logic owns filterValue; don't let foundation-filters sync interfere.
      return;
    }
    // If something stripped the correlation clause while upload mode is still
    // active (e.g. external setter), re-apply it. Avoids losing the upload
    // slice when other filters mutate criteria through 2-way bindings.
    if (
      this.uploadCorrelationId &&
      !this.hasCorrelationCriteria(this.filterValue)
    ) {
      this.filterValue = this.ensureStickyCorrelationCriteria(this.filterValue);
    }
  }

  /** Strip the correlation clause from criteria when upload mode is exited. */
  uploadCorrelationIdChanged(oldValue: string | null, newValue: string | null) {
    if (this.isBondsDatasourceSuspended) {
      return;
    }
    if (!newValue && oldValue) {
      const stripped = stripCorrelationIdFromCriteria(this.filterValue);
      if (stripped !== this.filterValue) {
        this.filterValue = stripped;
      }
    }
  }

  async watchlistValueChanged() {
    if (this.pendingRestoreState) {
      if (this.watchlistCriteria && this.pendingRestoreState.watchlistSelectedLabel) {
        this.watchlistCriteria.selectedLabel = this.pendingRestoreState.watchlistSelectedLabel;
      }
      return;
    }
    // Capture base CUSIPs before merge strips them (e.g. first ON after flyout apply).
    if (this.watchlistValue && this.baseCusips === null) {
      const cusips = extractCusipEqualityValues(this.filterValue);
      this.baseCusips = cusips.length ? cusips : null;
    }
    this.updateFilterValue(await this.mergedCriteria());
  }

  @observable bidModeCriteriaValue = '';
  bidModeCriteriaValueChanged() {
    if (this.pendingRestoreState) {
      if (this.bidModeCriteria && this.pendingRestoreState.bidModeSelectedLabel) {
        this.bidModeCriteria.selectedLabel = this.pendingRestoreState.bidModeSelectedLabel;
      }
      return;
    }
    if (!this.gridApi) {
      this.calculateTotalQuantity();
      return;
    }
    this.gridApi.onFilterChanged();
    this.calculateTotalQuantity();
    this.saveFilterState();
  }

  @observable timeModeCriteriaValue = '';
  timeModeCriteriaValueChanged() {
    if (this.pendingRestoreState) {
      if (this.timeModeCriteria && this.pendingRestoreState.timeModeSelectedLabel) {
        this.timeModeCriteria.selectedLabel = this.pendingRestoreState.timeModeSelectedLabel;
      }
      return;
    }
    const rawStripped = this.filterValue.replace(/(^|\s*&&?\s*)\(*\s*STATUS\s*==\s*['\"][^'\"]*['\"]\s*\)*/g, '');
    const baseFilter = sanitizeCriteriaString(rawStripped);
    let depth = 0;
    let hasTopLevelOr = false;
    for (let i = 0; i < baseFilter.length - 1; i++) {
      if (baseFilter[i] === '(') depth++;
      else if (baseFilter[i] === ')') depth--;
      else if (depth === 0 && baseFilter[i] === '|' && baseFilter[i + 1] === '|') { hasTopLevelOr = true; break; }
    }
    const wrappedBase = hasTopLevelOr && baseFilter ? `(${baseFilter})` : baseFilter;
    let newFilterValue = wrappedBase +
      (this.timeModeCriteriaValue ? (wrappedBase ? ' && ' : '') + this.timeModeCriteriaValue : '');
    this.updateFilterValue(newFilterValue);
  }

  updateFilterValue(newFilterValue: string) {
    this.filterValue = this.ensureStickyCorrelationCriteria(newFilterValue);
    if (this.restorePhase === 'idle') {
      this.saveFilterState();
    }
  }

  /** Avoids criteria churn when the string is unchanged (prevents grid refetch loops with watchlist). */
  private updateFilterValueIfChanged(newFilterValue: string) {
    const next = this.ensureStickyCorrelationCriteria(newFilterValue);
    if (next === this.filterValue) return;
    this.filterValue = next;
    if (this.restorePhase === 'idle') {
      this.saveFilterState();
    }
  }

  openFiltersFlyout() {
    this.filtersFlyoutOpen = true;
  }

  closeFiltersFlyout() {
    this.filtersFlyoutOpen = false;
  }

  async connectedCallback() {
    this.pendingRestoreState = this.getFilterCache().load();
    const hasStateToRestore = !!this.pendingRestoreState;

    if (hasStateToRestore) {
      this.isBondsCriteriaReady = false;
      this.restorePhase = 'criteria';
    }

    super.connectedCallback();

    if (hasStateToRestore) {
      requestAnimationFrame(() => {
        this.applyRestoredFilterState();
        this.isBondsCriteriaReady = true;
        void this.restoreSavedFilterDropdownSelection();
      });
    }
    this.addEventListener('openAuditFlyout', this.handleOpenAuditFlyout);
  }

  private saveFilterState(): void {
    if (!this.isConnected) return;
    // Never persist CORRELATION_ID. A page reload always reverts to the
    // live ACTIVE_MUNICIPAL_BONDS resource (the correlation rows server-side
    // belong to the previous browsing session and would no longer match).
    const state: SavedFilterState = {
      // Persist base form (watchlist stripped) so restore re-derives baseCusips correctly.
      filterValue: stripCorrelationIdFromCriteria(
        this.watchlistValue
          ? buildWatchlistMergedCriteria(this.filterValue, this.baseCusips, null)
          : this.filterValue
      ),
      filterName: this.foundationFilters?.filterName || this.filterName,
      timeModeCriteriaValue: this.timeModeCriteriaValue,
      bidModeCriteriaValue: this.bidModeCriteriaValue,
      watchlistValue: this.watchlistValue,
      filtersData: this.foundationFilters?.data ?? null,
      bidModeSelectedLabel: this.bidModeCriteria?.selectedLabel ?? null,
      timeModeSelectedLabel: this.timeModeCriteria?.selectedLabel ?? null,
      watchlistSelectedLabel: this.watchlistCriteria?.selectedLabel ?? null,
      bidTimeHoursMin: this.bidTimeHoursMin,
      bidTimeHoursMax: this.bidTimeHoursMax,
    };
    this.getFilterCache().save(state);
  }

  private applyRestoredFilterState(): void {
    const state = this.pendingRestoreState;
    if (!state) return;

    this.filterName = state.filterName || 'Apply Filters';

    if (this.foundationFilters && state.filtersData) {
      const fd = { ...state.filtersData } as Record<string, unknown>;
      delete fd.FIRM_FOR_HOURS;
      delete fd.FIRM_FOR_MIN;
      delete fd.FIRM_FOR_MAX;
      if (state.bidTimeHoursMin != null || state.bidTimeHoursMax != null) {
        fd.FIRM_FOR = {
          type: 'number',
          min: state.bidTimeHoursMin ?? undefined,
          max: state.bidTimeHoursMax ?? undefined,
        };
      }
      this.foundationFilters.data = fd;
    }
    if (this.bidModeCriteria && state.bidModeSelectedLabel) {
      this.bidModeCriteria.selectedLabel = state.bidModeSelectedLabel;
    }
    if (this.timeModeCriteria && state.timeModeSelectedLabel) {
      this.timeModeCriteria.selectedLabel = state.timeModeSelectedLabel;
    }
    if (this.watchlistCriteria && state.watchlistSelectedLabel) {
      this.watchlistCriteria.selectedLabel = state.watchlistSelectedLabel;
    }
    // Assign while pendingRestore blocks watchlistValueChanged; rematch after clear below.
    this.watchlistValue = state.watchlistValue || '';

    let restoredCriteria = state.filterValue || '';
    restoredCriteria = stripBidTimeHoursFormFieldsFromCriteria(restoredCriteria);
    restoredCriteria = stripBidTimeMsCriteriaFromCriteria(restoredCriteria);
    restoredCriteria = stripCorrelationIdFromCriteria(restoredCriteria);
    this.uploadCorrelationId = null;
    this.filterValue = sanitizeCriteriaString(restoredCriteria);

    this.bidTimeHoursMin = state.bidTimeHoursMin ?? null;
    this.bidTimeHoursMax = state.bidTimeHoursMax ?? null;
    this.filterValue = appendBidTimeMsCriteria(
      this.filterValue || '',
      this.bidTimeHoursMin,
      this.bidTimeHoursMax
    );

    // Prefer form CUSIP (user intent); fall back to criteria extract from persisted base.
    if (state.filtersData && 'CUSIP' in state.filtersData) {
      this.baseCusips = cusipsFromFilterData(state.filtersData);
    } else {
      const restoredCusips = extractCusipEqualityValues(this.filterValue);
      this.baseCusips = restoredCusips.length ? restoredCusips : null;
    }

    this.pendingRestoreState = null;
    if (this.watchlistValue) {
      void this.mergedCriteria().then((criteria) => this.updateFilterValueIfChanged(criteria));
    }

    this.gridApi?.onFilterChanged();
  }

  private async restoreSavedFilterDropdownSelection(): Promise<void> {
    const filterName = this.filterName;
    if (!filterName || filterName === 'Apply Filters' || !this.foundationFilters) {
      this.restorePhase = 'idle';
      return;
    }

    this.restorePhase = 'dropdown';
    const preservedCriteria = this.filterValue;
    const preservedData = this.foundationFilters.data
      ? { ...(this.foundationFilters.data as Record<string, unknown>) }
      : null;

    await restoreSavedFilterDropdown(
      this.foundationFilters,
      filterName,
      preservedCriteria,
      preservedData,
      (criteria) => {
        if (criteria !== this.filterValue) {
          this.filterValue = criteria;
        }
      },
    );
    this.restorePhase = 'idle';
  }

  public gridOptions = {
    getRowId: (params) => {
      const data = params.data;
      if (data?.ROW_REF && data.BOND_ID) {
        this.rowRefMap.set(data.ROW_REF, data.BOND_ID);
      }
      if (data?.BOND_ID) return data.BOND_ID;
      if (data?.ROW_REF) {
        return this.rowRefMap.get(data.ROW_REF) ?? `ROW_REF:${data.ROW_REF}`;
      }
      return '';
    },
    rowSelection: 'multiple',
    isExternalFilterPresent: () => !!this.bidModeCriteriaValue,
    doesExternalFilterPass: (node) => {
      const row = node.data ?? {};

      if (this.bidModeCriteriaValue) {
        const bidId = row.BID_ID;
        const isBlank = bidId == null || bidId === '';
        if (this.bidModeCriteriaValue === '((BID_PRICE == null))') {
          // Not Bid: show rows with no BID_ID
          if (!isBlank) return false;
        }
        if (this.bidModeCriteriaValue === '((BID_PRICE != null))') {
          // Bid: show rows with a BID_ID
          if (isBlank) return false;
        }
      }

      return true;
    },
    defaultCsvExportParams: {
      processCellCallback: (params) => {
        // Format time fields the same way as valueFormatter
        if (params.column.getColId() === 'RECEIVED_TIME' || 
            params.column.getColId() === 'DUE_TIME' || 
            params.column.getColId() === 'FIRM_TIME') {
          return params.value ? formatTime(params.value) : '';
        }
        
        // Format numeric fields
        if (params.column.getColId() === 'COUPON_RATE') {
          return formatNumeric(params.node?.data?.COUPON_RATE);
        }
        if (params.column.getColId() === 'CALL_PRICE') {
          return formatNumeric(params.node?.data?.CALL_PRICE);
        }
        if (params.column.getColId() === 'OUTSTANDING_VOLUME') {
          return formatNumeric(params.node?.data?.OUTSTANDING_VOLUME);
        }
        if (params.column.getColId() === 'IOI_SIZE') {
          return formatNumeric(params.node?.data?.IOI_SIZE);
        }
        
        // Format date fields
        if (params.column.getColId() === 'MATURITY_DATE' ||
            params.column.getColId() === 'PRE_REFUND_DATE' ||
            params.column.getColId() === 'CALL_DATE' ||
            params.column.getColId() === 'WORKOUT_DATE' ||
            params.column.getColId() === 'SETTLEMENT_DATE') {
          return params.value ? formatDate(params.value) : '';
        }        
        
        // Return the original value for all other fields
        return params.value;
      }
    },
    onCellValueChanged: (event) => {
      if (event.column.getColId() === 'BID_PRICE' || event.column.getColId() === 'BID_PRICE_TYPE') {
        // Force the per-row Submit button to redraw and refresh Submit All eligibility
        event.api.redrawRows({ rowNodes: [event.node] });
        this.updateEligibleBidsForSubmitAll();
      }
    },   
    onCellEditingStopped: (event) => {
      const field = event.column.getColId();
      if (field === 'BID_PRICE') {
        const raw = parseFloat(event.value);
        if (!isNaN(raw)) {
          const type = event.data?.BID_PRICE_TYPE;
          let formatted: string;
          if (type === 'Spread') {
            formatted = Math.round(raw).toString();
          } else {
            formatted = raw.toFixed(3);
          }
          if (formatted !== event.value) {
            event.node.setDataValue('BID_PRICE', formatted);
          }
        }
      }
    },
    onGridReady: (params) => {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      this.gridApi?.setSideBarVisible(false);
      this.gridApi?.setSideBar(sideBar);
      this.gridApi.addEventListener('cellDoubleClicked', this.handleGridCellDblClick);
      this.statusRefreshInterval = setInterval(() => {
        this.api?.refreshCells({ columns: ['STATUS'], force: true });
      }, 60000);
      this.subscribeToBids();
      this.subscribeToUserWatchlist();
      this.calculateTotalQuantity();
    },
    onModelUpdated: () => {
      this.updateWatchlistGrid();
      // Merge pending stream activity when grid rows become available
      if (this.activityMap.size > 0) {
        this.scheduleBidActivityFlush();
      }
      this.calculateTotalQuantity();
      this.updateEligibleBidsForSubmitAll();
    },
    onFilterChanged: () => {
      this.updateEligibleBidsForSubmitAll();
    },
    onCellEditingStarted: (params) => {
      const columnField = params.column.getColDef().field;
      
      if (columnField === 'BID_PRICE') {
        params.data._originalBidPrice = params.value;
      } else if (columnField === 'BID_PRICE_TYPE') {
        params.data._originalBidPriceType = params.value;
      }
    },
    onCellFocused: (event) => {
      const colId = event?.column?.getColId?.();
      const rowIndex = event?.rowIndex;
      if (colId !== 'SUBMIT_BID' || typeof rowIndex !== 'number' || !this.gridApi) return;

      window.setTimeout(() => {
        const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
        const instances =
          (this.gridApi as any)?.getCellRendererInstances?.({
            rowNodes: rowNode ? [rowNode] : undefined,
            columns: ['SUBMIT_BID'],
          }) || [];
        for (const inst of instances) {
          inst?.focus?.();
        }
      }, 0);
    },
    statusBar: {
      statusPanels: [
        {
          statusPanel: LabelValueStatusBarComponent,
          key: 'labelValue',
          align: 'left',
          statusPanelParams: {
            label: 'Total Quantity',
            value: 0,
            hide: false,
          },
        },
      ],
    },
  }

  private cptyBondActivitySubscription: any = null;

  /** Resets all filter controls (criteria + foundation) to default state. */
  private resetAllFilterControls(): void {
    // Exit upload mode and drop correlation criteria in one step before any
    // control changes that call updateFilterValue (watchlist/time/bid mode).
    // Otherwise the grid briefly binds ACTIVE_MUNICIPAL_BONDS with a
    // CORRELATION_ID clause left over from upload mode.
    this.uploadCorrelationId = null;
    this.filterValue = '';
    this.baseCusips = null;
    this.bidModeCriteriaValue = '';
    if (this.bidModeCriteria) this.bidModeCriteria.selectedLabel = undefined;
    this.bidTimeHoursMin = null;
    this.bidTimeHoursMax = null;
    this.timeModeCriteriaValue = '';
    if (this.timeModeCriteria) this.timeModeCriteria.selectedLabel = undefined;
    this.watchlistValue = '';
    if (this.watchlistCriteria) this.watchlistCriteria.selectedLabel = undefined;
    if (this.foundationFilters) this.foundationFilters.clearFiltersData();
    this.filterName = 'Apply Filters';
  }

  /**
   * Unmounts the genesis datasource, applies state changes, then remounts on the
   * next frame so resource-name/criteria never disagree during Clear All or upload.
   */
  private async transitionBondsDatasource(apply: () => void): Promise<void> {
    this.isBondsDatasourceSuspended = true;
    apply();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    this.isBondsDatasourceSuspended = false;
  }

  /**
   * Datasource resource name for the live activity grid.
   * Switches to the upload-backed view while a correlation is active so we
   * can render the full uploaded batch (potentially > 1000 rows) without
   * loading the entire municipal bond universe.
   */
  get activeBondsResourceName(): string {
    return this.uploadCorrelationId ? 'UPLOAD_FILTERED_BONDS' : 'ACTIVE_MUNICIPAL_BONDS';
  }

  get activeBondsOrderBy(): string {
    return this.uploadCorrelationId
      ? 'UPLOAD_FILTERED_BONDS_BY_DUE_TIME'
      : 'ACTIVE_MUNICIPAL_BONDS_BY_DUE_TIME';
  }

  /**
   * Grid page size. Raised in upload mode so a >1000-row uploaded batch can be
   * rendered (and exported / submitted) without server-side paging clipping.
   */
  get activeBondsMaxRows(): string {
    return this.uploadCorrelationId ? '15000' : '1000';
  }

  private static readonly CORRELATION_ID_PRESENCE_REGEX =
    /CORRELATION_ID\s*==\s*['"][^'"]+['"]/i;

  private hasCorrelationCriteria(criteria: string): boolean {
    return BondBiddingRoute.CORRELATION_ID_PRESENCE_REGEX.test(criteria || '');
  }

  /**
   * Ensures the sticky `CORRELATION_ID == '...'` clause is present whenever
   * upload mode is active. Removes any stale correlation clause first so we
   * never end up with conflicting CORRELATION_IDs in the same criteria.
   */
  private ensureStickyCorrelationCriteria(criteria: string): string {
    const baseCriteria = stripCorrelationIdFromCriteria(criteria);
    if (!this.uploadCorrelationId) {
      return baseCriteria;
    }
    const correlationClause = buildCorrelationIdFilter(this.uploadCorrelationId);
    if (!correlationClause) return baseCriteria;
    return sanitizeCriteriaString(
      baseCriteria ? `${baseCriteria} && ${correlationClause}` : correlationClause
    );
  }

  private calculateTotalQuantity() {
    if (!this.gridApi) return;

    let totalQuantity = 0;
    
    (this.gridApi as any).forEachNodeAfterFilterAndSort?.((rowNode: any) => {
      if (!rowNode.group && rowNode.data && rowNode.data.IOI_SIZE != null) {
        const qty = parseFloat(rowNode.data.IOI_SIZE) || 0;
        totalQuantity += qty;
      }
    });

    const statusBarLabelValue = this.gridApi.getStatusPanel(GridProStatusBarTypes.labelValue);
    if (statusBarLabelValue) {
      (statusBarLabelValue as any).updateParams?.({
        value: formatNumeric(totalQuantity),
      });
    }
  }

  private scheduleBidActivityFlush(): void {
    if (this.bidActivityFlushRafId != null) return;
    this.bidActivityFlushRafId = requestAnimationFrame(() => {
      this.bidActivityFlushRafId = null;
      this.updateBidActivity();
    });
  }

  private cancelScheduledBidActivityFlush(): void {
    if (this.bidActivityFlushRafId != null) {
      cancelAnimationFrame(this.bidActivityFlushRafId);
      this.bidActivityFlushRafId = null;
    }
  }

  private teardownCptyBondActivityStream(): void {
    if (this.cptyBondActivitySubscription) {
      this.cptyBondActivitySubscription.unsubscribe();
      this.cptyBondActivitySubscription = null;
    }
  }

  private teardownWatchlistStream(): void {
    if (this.watchlistSubscription) {
      this.watchlistSubscription.unsubscribe();
      this.watchlistSubscription = null;
    }
  }

  private subscribeToBids() {
    
    if (this.cptyBondActivitySubscription == null) {

      this.activityMap = new Map();

      this.cptyBondActivitySubscription = this.connect
        .stream(
          'CPTY_BOND_ACTIVITY',
          (message: Message) => {

            if (message.MESSAGE_TYPE === 'LOGOFF_ACK' && message.LOGGED_OUT === true) {
              this.teardownCptyBondActivityStream();
              return;
            }

            if (!message.ROW || !this.gridApi) return;

            for (const row of message.ROW) {
              const { DETAILS } = row;
              const { OPERATION, ROW_REF } = DETAILS;

              switch (OPERATION) {
                case 'MODIFY':
                  this.handleModifyOperation(row, ROW_REF);
                  break;
                case 'DELETE':
                  this.handleDeleteOperation(ROW_REF);
                  break;
                default:
                  this.handleNewOperation(row, ROW_REF);
                  break;
              }
            }
            
            this.scheduleBidActivityFlush();
          },
          (err) => { console.error('CPTY_BOND_ACTIVITY stream error', err); },
          { MAX_ROWS: 100000, MAX_VIEW: 100000 }
        ).subscribe();
    }
  }

  handleUploadValidationClosed() {
    this.uploadValidationOpen = false;
    this.uploadValidationFlyout?.cancel?.();
  }

  private handleGridCellDblClick = (e) => {

    // Don't open flyout for users with ViewPlatformActivity permission
    if (this.auth?.currentUser?.hasPermission('ViewPlatformActivity')) {
      return;
    }
    
    // Only open the flyout if not the WATCHING column
    if (
      e &&
      e.colDef &&
      (e.colDef.field === 'WATCHING' ||
        e.colDef.field === 'BID_PRICE' ||
        e.colDef.field === 'BID_PRICE_TYPE' ||
        e.colDef.field === 'SUBMIT_BID' ||
        e.colDef.field === 'AUDIT')
    ) {
      return;
    }
    if (e && e.data && e.data.BOND_ID) {
      this.selectedBondId = e.data.BOND_ID;
    }
    this.trancheDetail.setRowData(e.data);
    // Always open in Overview tab when double-clicking
    this.trancheDetail.setActiveTab('overview');
    this.bondDetailsOpen = true;
  };

  private handleOpenAuditFlyout = (event: CustomEvent) => {
    const { bondData, openWithAuditTab } = event.detail;
    
    if (bondData && bondData.BOND_ID) {
      this.selectedBondId = bondData.BOND_ID;
      this.trancheDetail.setRowData(bondData);
      
      // Set the active tab to audit history
      if (openWithAuditTab) {
        this.trancheDetail.setActiveTab('history');
      }
      
      this.bondDetailsOpen = true;
    }
  };

  handleFlyoutClosed() {
    this.trancheDetail?.onFlyoutClosed?.();
    this.bondDetailsOpen = false;
  }

  private getGridRowData(bondId: string): Record<string, unknown> | null {
    const rowNode = this.gridApi?.getRowNode(bondId);
    return rowNode?.data ?? null;
  }

  /** Merge stream activity onto a row without wiping fields missing from partial updates. */
  private applyBidActivityFields(
    target: Record<string, unknown>,
    source: Record<string, unknown>
  ): boolean {
    let changed = false;
    for (const field of BondBiddingRoute.BID_ACTIVITY_FIELDS) {
      if (source[field] !== undefined && target[field] !== source[field]) {
        target[field] = source[field];
        changed = true;
      }
    }
    if (
      source._originalBidPrice !== undefined &&
      target._originalBidPrice !== source._originalBidPrice
    ) {
      target._originalBidPrice = source._originalBidPrice;
      changed = true;
    }
    if (
      source._originalBidPriceType !== undefined &&
      target._originalBidPriceType !== source._originalBidPriceType
    ) {
      target._originalBidPriceType = source._originalBidPriceType;
      changed = true;
    }
    return changed;
  }

  private ensureActivityEntry(bondId: string, streamRow?: Record<string, unknown>): Record<string, unknown> {
    const existing = this.activityMap.get(bondId);
    const gridRow = this.getGridRowData(bondId);
    const merged = {
      ...(gridRow ?? {}),
      ...(existing ?? {}),
      ...(streamRow ?? {}),
      BOND_ID: bondId,
    };
    this.activityMap.set(bondId, merged);
    return merged;
  }

  private handleModifyOperation(row: any, rowRef: string) {
    let bondId = this.rowRefMap.get(rowRef);
    if (!bondId && row.BOND_ID) {
      bondId = row.BOND_ID;
      this.rowRefMap.set(rowRef, bondId);
    }
    if (!bondId) return;

    const activity = this.ensureActivityEntry(bondId, row);
    // Sync "original" to current server state so row shows no pending change and Edit/Delete stay visible
    if (activity.BID_PRICE !== undefined) {
      activity._originalBidPrice = activity.BID_PRICE;
    }
    if (activity.BID_PRICE_TYPE !== undefined) {
      activity._originalBidPriceType = activity.BID_PRICE_TYPE;
    }
  }

  private syncSubmittedBidToActivity(
    bondId: string,
    bidPrice: string,
    bidPriceType: string
  ): void {
    const activity = this.ensureActivityEntry(bondId);
    activity.BID_PRICE = bidPrice;
    activity.BID_PRICE_TYPE = bidPriceType;
    activity._originalBidPrice = bidPrice;
    activity._originalBidPriceType = bidPriceType;
  }

  /** Clears upload/edit pending state on grid rows after submit (matches single-row Submit). */
  private markBondRowsSubmitted(bondIds: string[]): void {
    const api = this.api;
    if (!api || bondIds.length === 0) return;

    const updatedRows: any[] = [];
    for (const bondId of bondIds) {
      const rowNode = api.getRowNode(bondId);
      if (!rowNode?.data) continue;
      updatedRows.push(getRowDataAfterSingleSubmit(rowNode.data, { success: true }));
    }
    if (updatedRows.length) {
      api.applyTransaction({ update: updatedRows });
    }
  }

  private handleBidSubmitted(bondId: string, bidPrice: string, bidPriceType: string): void {
    this.syncSubmittedBidToActivity(bondId, bidPrice, bidPriceType);
    this.updateBidActivity();
  }

  private handleDeleteOperation(rowRef: string) {
    const bondId = this.rowRefMap.get(rowRef);
    if (!bondId) return;

    const activity = this.activityMap.get(bondId);
    if (activity) {
      for (const field of BondBiddingRoute.BID_ACTIVITY_FIELDS) {
        activity[field] = null;
      }
    }
  }

  private handleNewOperation(row: any, rowRef: string) {
    if (!row.BOND_ID) return;
    this.rowRefMap.set(rowRef, row.BOND_ID);
    this.ensureActivityEntry(row.BOND_ID, row);
  }

  disconnectedCallback() {
    if (this.statusRefreshInterval) {
      clearInterval(this.statusRefreshInterval);
      this.statusRefreshInterval = null;
    }
    this.cancelScheduledBidActivityFlush();
    this.teardownCptyBondActivityStream();
    this.teardownWatchlistStream();
    super.disconnectedCallback?.();
  }

  markFoundationFiltersSearchRequested() {
    this.applyTimeFiltersOnNextChange = true;
  }

  private isExplicitFoundationSearch(event: CustomEvent): boolean {
    const detail = (event as any)?.detail as Record<string, unknown> | undefined;
    const action = String(
      detail?.action ?? detail?.type ?? detail?.trigger ?? detail?.eventType ?? ''
    ).toLowerCase();
    if (
      action.includes('search') ||
      action.includes('submit') ||
      action.includes('apply') ||
      detail?.isSearch === true ||
      detail?.isSubmit === true ||
      detail?.applied === true
    ) {
      return true;
    }
    if (this.applyTimeFiltersOnNextChange) {
      this.applyTimeFiltersOnNextChange = false;
      return true;
    }
    return false;
  }

  private reapplyTopFilterCriteriaAfterFoundationSearch(): void {
    // Foundation search can emit criteria built only from flyout controls.
    // Re-apply active top-bar criteria so segmented controls remain effective.
    if (this.timeModeCriteriaValue) {
      this.timeModeCriteriaValueChanged();
    }
  }

  async handleFilterChange(event: CustomEvent) {
    if (this.restorePhase !== 'idle') return;
    const filterName = (event.target as Filters)?.filterName;
    if (filterName) {
      this.filterName = filterName;
    } else {
      this.filterName = 'Apply Filters';
    }

    // Use the new value from the component so we have the latest criteria (sync may not have run yet)
    const rawCriteria = (event.target as any)?.value ?? this.filterValue;
    const currentCriteria = typeof rawCriteria === 'string' ? rawCriteria : '';

    const filterData = (this.foundationFilters?.data ?? null) as Record<string, unknown> | null;
    const clearedInvalidDueWithin = clearInvalidDueWithinInput(filterData);
    if (clearedInvalidDueWithin && this.foundationFilters) {
      // Re-assign to trigger form re-render and clear the visible control.
      this.foundationFilters.data = { ...(this.foundationFilters.data ?? {}) };
    }
    const applyTimeFiltersNow = this.isExplicitFoundationSearch(event);
    const transformed = applyBondBiddingFilterCriteria({
      currentCriteria,
      previousCriteria: this.filterValue || '',
      filterData,
      applyTimeFiltersNow,
      currentBidTimeHoursMin: this.bidTimeHoursMin,
      currentBidTimeHoursMax: this.bidTimeHoursMax,
    });
    this.bidTimeHoursMin = transformed.bidTimeHoursMin;
    this.bidTimeHoursMax = transformed.bidTimeHoursMax;
    const newFilterValue = transformed.criteria;

    if (this.watchlistValue) {
      // Form CUSIP is source of truth while watchlist ON (criteria may already be merged via sync).
      // clearFiltersData() drops the CUSIP key entirely — must reset baseCusips or merge keeps the old subset.
      if (filterData && 'CUSIP' in filterData) {
        this.baseCusips = cusipsFromFilterData(filterData);
      } else {
        this.baseCusips = null;
      }
      this.updateFilterValue(await this.mergedCriteria(newFilterValue));
    } else {
      const cusips = extractCusipEqualityValues(newFilterValue);
      this.baseCusips = cusips.length ? cusips : null;
      if (newFilterValue !== this.filterValue) {
        this.updateFilterValue(newFilterValue);
      } else {
        this.saveFilterState();
      }
    }

    this.reapplyTopFilterCriteriaAfterFoundationSearch();
  }

  get getBondBiddingJsonSchema() {
    return {
      type: 'object',
      properties: {
        CUSIP: {
          type: 'string',
          description: 'kotlin.String',
        },
        BOND_ID: {
          type: 'string',
          description: 'kotlin.String',
        },
        CALL_DATE: {
          type: 'number',
          description: 'org.joda.time.DateTime',
        },
        CALL_PRICE: {
          type: 'number',
          description: 'kotlin.Double',
        },
        COUPON_RATE: {
          type: 'number',
          description: 'kotlin.Double',
        },
        DUE_TIME: {
          type: 'string',
          description: 'time',
        },
        FIRM_TIME: {
          type: 'string',
          description: 'time',
        },
        IOI_SIZE: {
          type: 'number',
          description: 'kotlin.Double',
        },
        ISSUER_NAME: {
          type: 'string',
          description: 'kotlin.String',
        },
        SOURCE: {
          type: 'string',
          description: 'kotlin.String',
          enum: ['Institutional', 'Retail', ''],
        },
        ISSUER_RATINGS: {
          type: 'string',
          description: 'kotlin.String',
        },
        MATURITY_DATE: {
          type: 'number',
          description: 'org.joda.time.DateTime',
        },
        OUTSTANDING_VOLUME: {
          type: 'number',
          description: 'kotlin.Double',
        },
        PRE_REFUND_DATE: {
          type: 'number',
          description: 'org.joda.time.DateTime',
        },
        RECEIVED_TIME: {
          type: 'string',
          description: 'time',
        },
        SECTOR: {
          type: 'string',
          description: 'kotlin.String',
        },
        SELLER_ID: {
          type: 'string',
          description: 'kotlin.String',
        },
        SETTLEMENT_DATE: {
          type: 'number',
          description: 'org.joda.time.DateTime',
        },
        STATE: {
          type: 'string',
          description: 'kotlin.String',
        },
        STATUS: {
          type: 'string',
          description: 'kotlin.String',
        },
        TAX_STATUS: {
          type: 'string',
          description: 'kotlin.String',
        },
        WORKOUT_DATE: {
          type: 'number',
          description: 'org.joda.time.DateTime',
        },
        DUE_WITHIN_AMOUNT: {
          type: 'string',
          description: 'kotlin.String',
        },
        FIRM_FOR: {
          type: 'number',
          description: 'kotlin.Long',
        },
      },
    };
  }

  @volatile
  get getBondBiddingUiSchema(): UiSchema {
    return {
      type: 'VerticalLayout',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/BOND_ID',
          label: 'Reference#',
        },
        {
          type: 'Control',
          scope: '#/properties/CUSIP',
          options: <StringRendererOptions>{
            textarea: true,
          },
        },
        { type: 'Control', scope: '#/properties/CALL_DATE', label: 'Call Date' },
        { type: 'Control', scope: '#/properties/CALL_PRICE', label: 'Call Price' },
        { type: 'Control', scope: '#/properties/COUPON_RATE', label: 'Coupon Rate' },
        { type: 'Control', scope: '#/properties/DUE_TIME', label: 'Due Time' },
        { type: 'Control', scope: '#/properties/FIRM_TIME', label: 'Firm Time' },
        { type: 'Control', scope: '#/properties/DUE_WITHIN_AMOUNT', label: 'Due within (hours)' },
        { type: 'Control', scope: '#/properties/FIRM_FOR', label: 'Firm For (hours)' },
        { type: 'Control', scope: '#/properties/IOI_SIZE', label: 'Quantity' },
        { type: 'Control', scope: '#/properties/ISSUER_NAME', label: 'Issuer Name' },
        { type: 'Control', scope: '#/properties/SOURCE',label: 'Source' },
        { type: 'Control', scope: '#/properties/ISSUER_RATINGS', label: 'Issuer Ratings' },
        { type: 'Control', scope: '#/properties/MATURITY_DATE', label: 'Maturity Date' },
        { type: 'Control', scope: '#/properties/OUTSTANDING_VOLUME', label: 'Outstanding Volume' },
        { type: 'Control', scope: '#/properties/PRE_REFUND_DATE', label: 'Pre-refund Date' },
        { type: 'Control', scope: '#/properties/RECEIVED_TIME', label: 'Received Time' },
        { type: 'Control', scope: '#/properties/SECTOR', label: 'Sector' },
        { type: 'Control', scope: '#/properties/SETTLEMENT_DATE', label: 'Settlement Date' },
        { type: 'Control', scope: '#/properties/STATE', label: 'State' },
        { type: 'Control', scope: '#/properties/TAX_STATUS', label: 'Fed Taxable' },
        { type: 'Control', scope: '#/properties/WORKOUT_DATE', label: 'Workout Date' },
      ],
    };
  }

  @observable bidModeCriteria: CriteriaSegmentedControl;
  @observable timeModeCriteria: CriteriaSegmentedControl;
  @observable watchlistCriteria: CriteriaSegmentedControl;

  public async clearAllFilters(): Promise<void> {
    this.pendingRestoreState = null;
    this.restorePhase = 'idle';
    this.applyTimeFiltersOnNextChange = false;
    await this.transitionBondsDatasource(() => {
      this.resetAllFilterControls();
      this.getFilterCache().clear();
    });
    this.gridApi?.onFilterChanged();
  }

  /**
   * Downloads CSV for the Live Activity grid.
   *
   * - In upload mode (correlation-backed view) we export directly from the grid -
   *   the grid already holds exactly the uploaded slice and is bounded by the
   *   `activeBondsMaxRows` upload-mode page size, so this is "download what you see".
   * - Otherwise, we fall back to the backend export (up to 100k rows).
   */
  public async downloadAll(): Promise<void> {
    if (this.isDownloadInProgress) return;

    this.isDownloadInProgress = true;
    try {
      const api = this.api;

      const canUseGridExport = !!api && !!this.uploadCorrelationId;

      if (canUseGridExport) {
        const rows: any[] = [];
        (api as any).forEachNodeAfterFilterAndSort((node: any) => {
          if (node?.data) {
            rows.push(node.data);
          }
        });
        downloadAll(this.getExportColumns(), rows);
        return;
      }

      const exportResult = await this.fetchExportRows();
      if (exportResult.ok === false) {
        showBackendUnavailableToast(
          'download',
          exportResult.reason === 'nack' ? exportResult.message : undefined
        );
        return;
      }
      downloadAll(this.getExportColumns(), exportResult.rows);
    } finally {
      this.isDownloadInProgress = false;
    }
  }

  private fetchExportRows() {
    return fetchMergedExportRows({
      connect: this.connect,
      filterValue: this.filterValue,
      bidModeCriteriaValue: this.bidModeCriteriaValue,
      bidTimeHoursMin: this.bidTimeHoursMin,
      bidTimeHoursMax: this.bidTimeHoursMax,
    });
  }

  private getExportColumns(): Array<{ field: string; headerName: string }> {
    const allColumns = getLiveActivityColumns(this.auth?.currentUser);
    const skipFields = new Set(['WATCHING', 'SUBMIT_BID', 'AUDIT']);
    const mandatoryFields = new Set(['BOND_ID', 'BID_PRICE', 'BID_PRICE_TYPE']);

    const dataColumns = allColumns.filter(
      (col) => col.field && !skipFields.has(col.field) && !col.suppressColumnsToolPanel
    );

    const visibleColIds = new Set<string>();
    const api = (this.grid?.columnApi as ColumnApi) ?? this.columnApi;
    if (api) {
      const displayedCols = api.getAllDisplayedColumns?.();
      if (displayedCols?.length) {
        for (const col of displayedCols) {
          const colId = col.getColId?.();
          if (colId) visibleColIds.add(colId);
        }
      } else {
        const state = api.getColumnState?.();
        for (const col of state || []) {
          if (col.hide !== true && col.colId) visibleColIds.add(col.colId);
        }
      }
    }

    const ordered: Array<{ field: string; headerName: string }> = [];
    const added = new Set<string>();

    const addColumn = (col: any) => {
      if (col?.field && !added.has(col.field)) {
        added.add(col.field);
        ordered.push({
          field: col.field,
          headerName: col.headerName || col.field,
        });
      }
    };

    const useVisibleOnly = visibleColIds.size > 0;
    for (const col of dataColumns) {
      if (useVisibleOnly ? visibleColIds.has(col.field) : true) {
        addColumn(col);
      }
    }
    for (const field of mandatoryFields) {
      if (!added.has(field)) {
        const col = dataColumns.find((c) => c.field === field) || { field, headerName: field };
        addColumn(col);
      }
    }

    return ordered;
  }

  public upload(): void {
    if (this.uploadFileInput) {
      this.uploadFileInput.value = '';
      this.uploadFileInput.click();
    }
  }

  async handleUploadFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    input.value = '';

    if (!file) return;

    this.isUploadInProgress = true;

    try {
      // 1. Reset all filters (criteria, foundation, grid) before backend lookup
      await this.clearAllFilters();
      this.resetAllGridFilters();

      const parseResult = await parseUploadFile(file);
      if (!parseResult) return;

      const exportResult = await this.fetchExportRows();
      if (exportResult.ok === false) {
        showBackendUnavailableToast(
          'upload',
          exportResult.reason === 'nack'
            ? exportResult.message
            : BOND_BIDDING_MESSAGES.upload.validationRetry
        );
        return;
      }

      const validation = validateAgainstMergedRows(parseResult.rows, exportResult.rows);
      if (!validation) return;

      const { result, backendRowMap } = validation;

      const hasErrorsOrIgnored = result.errorRows > 0 || result.ignoredRows > 0;
      if (hasErrorsOrIgnored) {
        this.uploadValidationOpen = true;
        await new Promise((r) => requestAnimationFrame(r));
        const proceed = await this.uploadValidationFlyout?.open?.(result);
        this.uploadValidationOpen = false;
        if (!proceed) return;
      }

      // Switch the grid to UPLOAD_FILTERED_BONDS for the validated set BEFORE
      // applying row updates so apply-to-grid does not waste work on rows the
      // datasource is about to drop.
      const validBondIds = result.rows
        .filter((r) => r.existsInGrid && !r.hasValidationError)
        .map((r) => r.BOND_ID);
      const switchedToUploadMode = await this.applyUploadBatchToGridContext(
        validBondIds
      );
      if (!switchedToUploadMode) return;

      applyToGrid(
        result,
        backendRowMap,
        this.gridApi,
        this.activityMap,
        this.bondDetailsOpen,
        // Filtering is handled by the correlation switch above; no-op here so
        // applyToGrid does not re-apply BOND_ID set filters or foundation text.
        () => undefined,
        this.selectedBondId,
        (row) => this.trancheDetail?.setRowData?.(row)
      );
    } finally {
      this.isUploadInProgress = false;
    }
  }

  /** Resets all AG grid column filters. Call this first when starting an upload. */
  private resetAllGridFilters(): void {
    const api = this.api;
    if (api) {
      api.setFilterModel(null);
      (api as any).destroyFilter?.('BOND_ID');
      (api as any).onFilterChanged?.();
    }
  }

  /**
   * Switches the live activity grid to upload mode for the supplied bond IDs.
   *
   * Flow:
   *   1. Clear other filter controls so the upload slice is rendered cleanly.
   *   2. POST the IDs to EVENT_UPLOAD_BOND_FILTER, which inserts a row per ID
   *      under a fresh CORRELATION_ID and returns it to us.
   *   3. Set `uploadCorrelationId` (drives resource swap to UPLOAD_FILTERED_BONDS
   *      via `activeBondsResourceName` / `activeBondsOrderBy`).
   *   4. Push `CORRELATION_ID == '<uuid>'` into criteria. Subsequent filter
   *      changes are kept sticky by `ensureStickyCorrelationCriteria`.
   *
   * Returns true on success, false if the user-supplied list was empty or the
   * server rejected the event.
   */
  private async applyUploadBatchToGridContext(bondIds: string[]): Promise<boolean> {
    // 1. Clear all filters (foundation, criteria, grid) - grid already reset at upload start
    this.resetAllFilterControls();
    this.resetAllGridFilters();

    const safeBondIds = prepareUploadBondIds(bondIds);
    if (!safeBondIds) return false;

    const correlationId = await registerUploadBondFilter(this.connect, safeBondIds);
    if (!correlationId) return false;

    await this.transitionBondsDatasource(() => {
      this.uploadCorrelationId = correlationId;
      this.filterValue = this.ensureStickyCorrelationCriteria('');
    });
    return true;
  }

  private getSubmitAllReferenceIdSource(): string | null | undefined {
    if (this.uploadCorrelationId) {
      const api = this.api;
      return getUploadModeReferenceIdSource(api);
    }
    return this.foundationFilters?.data?.BOND_ID;
  }

  private updateEligibleBidsForSubmitAll(): void {
    const api = this.api;
    const { referenceIds, eligibleBids } = getSubmitAllContext(
      api,
      this.getSubmitAllReferenceIdSource()
    );
    this.hasEligibleBidsForSubmitAll = referenceIds.length > 0 && eligibleBids.length > 0;
  }

  public async submitAll(): Promise<void> {
    if (this.isSubmitAllInProgress) return;

    const api = this.api;
    if (!api) {
      showBulkToast(
        BOND_BIDDING_TOAST_TITLE.submitAll,
        BOND_BIDDING_MESSAGES.submitAll.gridNotReady,
        'error'
      );
      return;
    }
    const { referenceIds, eligibleBids: toSubmit } = getSubmitAllContext(
      api,
      this.getSubmitAllReferenceIdSource()
    );
    if (referenceIds.length === 0) {
      showBulkToast(
        BOND_BIDDING_TOAST_TITLE.submitAll,
        BOND_BIDDING_MESSAGES.submitAll.needReferenceFilter,
        'warning'
      );
      return;
    }
    if (toSubmit.length === 0) {
      showBulkToast(
        BOND_BIDDING_TOAST_TITLE.submitAll,
        BOND_BIDDING_MESSAGES.submitAll.noEligibleBids,
        'warning'
      );
      return;
    }

    this.isSubmitAllInProgress = true;
    try {
      const result = await submitBondBidsBatch(toSubmit);

      if (!result.success) {
        const errMsg =
          'error' in result ? result.error : BOND_BIDDING_MESSAGES.submitAll.requestFailedFallback;
        showBulkToast(BOND_BIDDING_TOAST_TITLE.submitAll, errMsg, 'error');
        return;
      }

      const succeeded = result.submittedBondIds.length;
      const failed = result.failedBids.length;

      const toSubmitMap = new Map(toSubmit.map((b) => [b.BOND_ID, b]));
      for (const bondId of result.submittedBondIds) {
        const bid = toSubmitMap.get(bondId);
        if (!bid) continue;
        this.syncSubmittedBidToActivity(bondId, bid.BID_PRICE, bid.BID_PRICE_TYPE);
      }
      // Clear grid pending-edit flags so CPTY_BOND_ACTIVITY stream updates are not blocked.
      this.markBondRowsSubmitted(result.submittedBondIds);
      this.cancelScheduledBidActivityFlush();
      this.updateBidActivity();

      if (failed === 0) {
        showBulkToast(
          BOND_BIDDING_TOAST_TITLE.submitAll,
          BOND_BIDDING_MESSAGES.submitAll.submitted(succeeded),
          'success'
        );
      } else {
        showBulkToast(
          BOND_BIDDING_TOAST_TITLE.submitAll,
          BOND_BIDDING_MESSAGES.submitAll.partialFailure(succeeded, failed),
          'error'
        );
      }
    } finally {
      this.isSubmitAllInProgress = false;
    }
  }

  private async updateBidActivity() {
    if (!this.gridApi || !this.activityMap.size) return;

    const updatedRows: any[] = [];
    const rowNodes: any[] = [];

    for (const [bondId, bidActivity] of this.activityMap) {
      const rowNode = this.gridApi.getRowNode(bondId);
      if (!rowNode?.data) continue;

      const currentData = rowNode.data;
      if (hasRowPendingChanges(currentData as any) || currentData.isEditing) {
        continue;
      }
      const updatedRow = { ...currentData };
      if (this.applyBidActivityFields(updatedRow, bidActivity)) {
        updatedRows.push(updatedRow);
        rowNodes.push(rowNode);
      }
    }

    if (updatedRows.length) {
      this.gridApi.applyTransaction({ update: updatedRows });
      this.updateEligibleBidsForSubmitAll();
      if (this.bondDetailsOpen && this.selectedBondId) {
        const selectedUpdate = updatedRows.find((r) => r.BOND_ID === this.selectedBondId);
        if (selectedUpdate) {
          this.trancheDetail.setRowData(selectedUpdate);
        }
      }
      if (rowNodes.length) {
        this.gridApi.redrawRows({ rowNodes });
      }
    }
  }

  private subscribeToUserWatchlist() {
    
    if (this.watchlistSubscription == null) {

      this.watchlistSubscription = this.connect
        .stream(
          'ALL_USER_WATCHLIST',
          (message: Message) => {

            if (message.MESSAGE_TYPE === 'LOGOFF_ACK' && message.LOGGED_OUT === true) {
              this.teardownWatchlistStream();
              return;
            }

            if (!message.ROW || !this.gridApi) return;

            this.connect.snapshot('ALL_USER_WATCHLIST').then((response) => {
              if (response.ROW && this.grid?.gridApi) {
                this.watchlistCusipSet = new Set(response.ROW.map(row => row.CUSIP));
                this.updateWatchlistGrid();
              }        
            });
          },
          (err) => { console.error('ALL_USER_WATCHLIST stream error', err); },
          { MAX_ROWS: 100000, MAX_VIEW: 100000 }
        )
        .subscribe();
      }
  }

  private async updateWatchlistGrid() {
    if (this.gridApi) {
      const updatedRows: any[] = [];
      this.gridApi.forEachNode((rowNode) => {
        if (rowNode.data && rowNode.data.CUSIP && this.watchlistCusipSet) {
          const isWatching = this.watchlistCusipSet.has(rowNode.data.CUSIP);
          if (!!rowNode.data.WATCHING !== isWatching) {
            rowNode.data.WATCHING = isWatching;
            updatedRows.push(rowNode.data);
          }
        }
      });
      if (updatedRows.length > 0) {
        this.gridApi.applyTransaction({ update: updatedRows });
        this.gridApi.refreshCells({
          rowNodes: updatedRows,
          columns: ['WATCHING'],
          force: true,
        });
      }

      if (this.watchlistValue) {
        this.updateFilterValueIfChanged(await this.mergedCriteria());
      }
    }
  }

  private async watchlistCusips(): Promise<string[]> {
    if (this.watchlistCusipSet) {
      return [...this.watchlistCusipSet].filter(Boolean) as string[];
    }
    const response = await this.connect.snapshot('ALL_USER_WATCHLIST');
    return (response.ROW ?? []).map((row) => row.CUSIP).filter(Boolean);
  }

  private async mergedCriteria(base = this.filterValue): Promise<string> {
    const wl = this.watchlistValue ? await this.watchlistCusips() : null;
    return buildWatchlistMergedCriteria(base, this.baseCusips, wl);
  }

}
